import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { isTauri } from "./web-runtime";
import { workoutsCsv, csvFilename } from "./export";
import { supabase } from "./supabase";
import { store, plateBreakdown, DEFAULT_BAR_KG, DEFAULT_BAR_LB, type Workout, type WorkoutSet, type WorkoutDetail, type ExercisePR, type Routine, type RoutineExercise, type PrevSet, type LiftSignal, type ProgramSession, type SessionPoint } from "./store";
import { ProgramSection } from "./ProgramFlow";
import {
  summarizeTrends, openWebLogin, BrainError,
  type TrendSummary,
} from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { confirmDestructive } from "./CloudBackup";
import type { FactTone } from "./store";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import { PhotoViewer } from "./PhotoViewer";
import { presetExercises, muscleGroups, presetBySlug } from "./exercises";
import { exerciseVisual, ExerciseIcon } from "./exerciseVisual";
import { Sparkline } from "./Sparkline";
import { ProgressRing } from "./ProgressRing";
import { Confetti } from "./Confetti";
import { CountUp } from "./CountUp";
import { roundedOneRepMax, prLabels, formatWeight } from "./policy";
import Chat from "./chat/Chat";
import { Lock, Check, Dumbbell, Sparkles, Trophy, Plus, Trash2, TrendingUp, TrendingDown, Minus, Flame, ChevronLeft, WifiOff, AlertTriangle, Wallet, RefreshCw, Info, X, ListChecks, Repeat, Calculator, Timer, Play, Pause, RotateCcw } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "";

/**
 * 'just the bar — no plates needed' — the bar itself is this app's unit.
 * The last of the shared lucide `Sparkles` launcher glyphs — 20 of the fleet's 26 chat launchers
 * rendered it in common, which is the shared-fingerprint half of Guideline 4.3(a). This app's
 * OUTLINE was already its own, so only the glyph needed to move.
 */
function BarMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M2.6 12h1.8M19.6 12h1.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <rect x="4.9" y="8.2" width="2.7" height="7.6" rx="0.9" stroke="currentColor" strokeWidth="1.6" />
      <rect x="16.4" y="8.2" width="2.7" height="7.6" rx="0.9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7.6 12h8.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (#62 / Apple 5.1.1i) — shown ONCE on first launch before anything else.
  const [aiConsent, setAiConsent] = useState(() => localStorage.getItem(AI_CONSENT_KEY) === "1");

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // Deep-link auth: web login returns workout-tracker://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login/home, before any AI call.
  if (!aiConsent) {
    return (
      <AiConsent
        brand="Oriora Workout"
        onAccept={() => { localStorage.setItem(AI_CONSENT_KEY, "1"); setAiConsent(true); }}
      />
    );
  }
  if (!mounted) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed) return <Login />;
  return <Home email={email} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div className="row" style={{ justifyContent: "center", color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)", gap: "0.4rem" }}>
          <Dumbbell size={18} /> Oriora Workout
        </div>
        <h1 style={{ fontSize: "1.4rem", margin: "0.5rem 0 0.4rem" }}>Your training, logged and read back</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — sign in, then you're back here. Your workouts live on this device. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (#61, Apple Guideline 4); desktop → web login + deep-link handback.
            try {
              const p = platform();
              if (p === "ios") await signInIos("workout-tracker");
              else if (p === "macos") await signInMac("workout-tracker");
              else if (p === "web") window.location.href = "/login?next=/workout-tracker/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

// ── Streak: count consecutive days (ending today/yesterday) with a workout ─────
function computeStreak(workouts: Workout[]): number {
  const days = new Set(workouts.map((w) => w.performed_on.slice(0, 10)));
  if (days.size === 0) return 0;
  let streak = 0;
  const cursor = new Date(todayISO() + "T00:00:00Z");
  // allow the streak to "start" from today OR yesterday (rest day grace until next miss)
  if (!days.has(cursor.toISOString().slice(0, 10))) cursor.setUTCDate(cursor.getUTCDate() - 1);
  while (days.has(cursor.toISOString().slice(0, 10))) {
    streak++;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return streak;
}

function Home({ email }: { email: string | null }) {
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [prs, setPRs] = useState<ExercisePR[]>([]);
  const [routines, setRoutines] = useState<Routine[]>([]);
  // Weekly progress ring (Apple Fitness+ / Fitbit pattern) — only ever against a REAL stated goal
  // (training_profile.days_per_week), never an invented target.
  const [weeklyGoal, setWeeklyGoal] = useState<number | null>(null);
  // Free, on-device (no AI call) — which lifts are trending down over the last 14 days.
  // Same aggregates the paid AI summary uses, surfaced directly as a ranked board instead
  // of only ever appearing as a count inside a summary sentence you have to pay to see.
  const [liftSignals, setLiftSignals] = useState<LiftSignal[]>([]);
  const [detailId, setDetailId] = useState<string | null>(null);
  // The current OPEN (unfinished) session, if any — powers the "Resume today's session" card.
  const [activeId, setActiveId] = useState<string | null>(null);
  // When a workout is started from a routine, its exercises seed the Log-a-set quick picks.
  const [seed, setSeed] = useState<RoutineExercise[] | undefined>(undefined);
  // Set when the current workout was started from a program's NEXT UP session — on leaving the
  // detail view, that session gets marked done (and its exercise's future targets adaptively
  // re-adjust, on-device — see complete_program_session in db.rs). Bumping programRefresh forces
  // ProgramSection to reload so the hero advances to whatever's next.
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [programRefresh, setProgramRefresh] = useState(0);
  // Bumped when chat recognizes "draft me a program" — ProgramSection watches this and opens
  // its own draft flow, same as tapping its "Draft a program" button.
  const [programDraftTrigger, setProgramDraftTrigger] = useState(0);
  const [showNew, setShowNew] = useState(false);
  // Offline banner — workouts/sets still persist to local SQLite; only the AI call pauses.
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Native-menu target: ⌘T (Summarize trends) fires the AI run inside TrendInsights.
  // `pendingSummarize` lets ⌘T work from the detail view: we leave the detail view first,
  // then run once TrendInsights has mounted on the list view (see the effect below).
  const summarizeRef = useRef<() => void>(() => {});
  const [pendingSummarize, setPendingSummarize] = useState(false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — it changes no
  // existing screen. onChanged → reload() refreshes the workout list after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);

  const reload = useCallback(async () => {
    const [w, p, r, sig, profile, active] = await Promise.all([
      store.listWorkouts(), store.listPRs(), store.listRoutines(), store.trendSignals(14), store.getTrainingProfile(), store.activeWorkoutId(),
    ]);
    setWorkouts(w); setPRs(p); setRoutines(r); setLiftSignals(sig?.per_lift ?? []); setWeeklyGoal(profile?.days_per_week ?? null); setActiveId(active);
  }, []);

  // CSV export of the whole training log. Added 2026-08-30: the parity matrix claimed this as a
  // BETTER row and sold it against a competitor's Pro-gated export, but it did not exist —
  // `BackupMenu` renders the button only when `onExportCsv` is passed, and nothing passed it.
  // Fleet pattern (same as Lockbox): web gets a blob download, native gets the save dialog.
  // ⚠️ Native needs `fs:allow-write-text-file` in capabilities — Workout had only `fs:default`,
  // so wiring this without it would have produced a dialog that silently wrote nothing.
  const doExportCsv = useCallback(() => {
    void (async () => {
      const ws = await store.listWorkouts();
      if (!ws.length) return;
      const details = await Promise.all(ws.map((w) => store.getWorkout(w.id)));
      const items = details
        .filter((d): d is NonNullable<typeof d> => !!d)
        .map((d) => ({ workout: d.workout, sets: d.sets }));
      const csv = workoutsCsv(items);
      const filename = csvFilename();
      if (!isTauri()) {
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        return;
      }
      const { save } = await import("@tauri-apps/plugin-dialog");
      const { writeTextFile } = await import("@tauri-apps/plugin-fs");
      const path = await save({ defaultPath: filename });
      if (path) await writeTextFile(path, csv);
    })().catch(() => { /* export is a convenience — never throw into the UI */ });
  }, []);

  // Start a fresh workout from a saved routine: create it, then hand its exercises
  // to the detail view so Log-a-set offers them as one-tap picks.
  const startFromRoutine = useCallback(async (r: Routine) => {
    const w = await store.createWorkout({ name: r.name, performed_on: todayISO() });
    await reload();
    setSeed(r.exercises);
    setDetailId(w.id);
  }, [reload]);

  // Chat entry point for "start my push day routine" — resolves the routine by id (chat
  // has no row to tap) and opens it pre-seeded, exactly like tapping the routine's Start.
  const startRoutineById = useCallback(async (id: string) => {
    const r = routines.find((x) => x.id === id);
    if (!r) return null;
    await startFromRoutine(r);
    return r;
  }, [routines, startFromRoutine]);

  // "Repeat workout" (Hevy/Strong's actual staple — recreate a past session instead of starting
  // from scratch): pull the exercises the workout actually contained (deduped, first-seen order)
  // and seed a brand-new workout with them today, same mechanism as a routine.
  const repeatWorkout = useCallback(async (w: Workout) => {
    const detail = await store.getWorkout(w.id);
    if (!detail) return null;
    const seen = new Set<string>();
    const exercises: RoutineExercise[] = [];
    for (const s of detail.sets) {
      if (seen.has(s.exercise_slug)) continue;
      seen.add(s.exercise_slug);
      exercises.push({ slug: s.exercise_slug, name: s.exercise_name });
    }
    const nw = await store.createWorkout({ name: w.name, performed_on: todayISO() });
    await reload();
    setSeed(exercises);
    setDetailId(nw.id);
    return nw;
  }, [reload]);

  // Chat entry point for the same feature ("repeat my last workout") — targets the most recent
  // workout since chat has no row to tap; `workouts` is already newest-first (listWorkouts).
  const repeatLastWorkout = useCallback(async () => {
    if (workouts.length === 0) return null;
    return repeatWorkout(workouts[0]);
  }, [workouts, repeatWorkout]);

  // Same shape, but for a program's planned session — Start pre-loads its exercises exactly like
  // a routine does, and completing the workout marks the session done (see onBack below).
  const startFromProgramSession = useCallback(async (session: ProgramSession) => {
    const w = await store.createWorkout({ name: session.label, performed_on: todayISO() });
    await reload();
    setSeed(session.exercises.map((e) => ({ slug: e.slug, name: e.name })));
    setPendingSessionId(session.id);
    setDetailId(w.id);
  }, [reload]);

  useEffect(() => { void reload(); }, [reload]);

  // Connectivity → offline banner (entries still save; AI calls pause).
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Native macOS menu → app actions. ⌘N opens New workout (from the list view);
  // ⌘T runs the AI trend summary. Separate from the FROZEN auth deep-link listener.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      if (e.payload === "new") { setDetailId(null); setShowNew(true); }
      else if (e.payload === "summarize") { setDetailId(null); setPendingSummarize(true); }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Run a pending ⌘T summary once the list view (and TrendInsights) is on screen.
  useEffect(() => {
    if (pendingSummarize && !detailId) {
      setPendingSummarize(false);
      summarizeRef.current();
    }
  }, [pendingSummarize, detailId]);

  const streak = useMemo(() => computeStreak(workouts), [workouts]);
  const last14 = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 14);
    const iso = cutoff.toISOString().slice(0, 10);
    return workouts.filter((w) => w.performed_on >= iso);
  }, [workouts]);
  const last7Count = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 6); // rolling 7-day window incl. today
    const iso = cutoff.toISOString().slice(0, 10);
    return workouts.filter((w) => w.performed_on >= iso).length;
  }, [workouts]);

  if (detailId) return <WorkoutDetailView id={detailId} seedExercises={seed} onBack={() => {
    if (pendingSessionId) {
      const sid = pendingSessionId; const wid = detailId;
      void store.completeProgramSession(sid, wid).then(() => setProgramRefresh((n) => n + 1));
    }
    setDetailId(null); setSeed(undefined); setPendingSessionId(null); void reload();
  }} />;

  return (
    <main style={{ maxWidth: 880, margin: "0 auto", padding: "max(2rem, calc(env(safe-area-inset-top) + 1rem)) max(1.5rem, env(safe-area-inset-right)) max(4rem, calc(env(safe-area-inset-bottom) + 2rem)) max(1.5rem, env(safe-area-inset-left))" }}>
      <div className="page-glow" aria-hidden />
      {activeId && !detailId && workouts.some((w) => w.id === activeId) && (() => {
        const aw = workouts.find((w) => w.id === activeId)!;
        return (
          <button className="card row" onClick={() => { setSeed(undefined); setDetailId(activeId); }}
            style={{ width: "100%", justifyContent: "space-between", alignItems: "center", padding: "0.9rem 1.1rem", marginBottom: "1rem", textAlign: "left", cursor: "pointer", borderColor: "var(--accent)" }}>
            <span className="row" style={{ gap: "0.6rem", alignItems: "center" }}>
              <Play size={18} style={{ color: "var(--accent)" }} />
              <span>
                <span className="display" style={{ fontWeight: 800, fontSize: "1rem", display: "block", lineHeight: 1.1 }}>Resume today's session</span>
                <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{aw.name}{aw.total_sets > 0 ? ` · ${aw.total_sets} set${aw.total_sets !== 1 ? "s" : ""} in` : " · not started"}</span>
              </span>
            </span>
            <ChevronLeft size={18} style={{ transform: "rotate(180deg)", color: "var(--ink-3)" }} />
          </button>
        );
      })()}
      {offline && (
        <div role="status" className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-xs)", color: "var(--amber)", background: "color-mix(in srgb, var(--amber) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--amber) 35%, transparent)", borderRadius: 8, padding: "0.45rem 0.75rem", marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0 }} /> Offline — workouts still save on this device; AI trends pause until you reconnect
        </div>
      )}
      {/* Slim wordmark header — the tally-and-streak hero is gone. Leading with a running count of
          "N workouts logged" + an "N-day streak" flame was the exact element that made a strength
          app read as a habit tracker and as a Mat Time clone; the a3 restructure retires it. */}
      <header className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem", flexWrap: "wrap", gap: "1rem" }}>
        <div className="row" style={{ alignItems: "baseline", gap: "0.4rem" }}>
          <Dumbbell size={18} style={{ color: "var(--accent)", alignSelf: "center" }} />
          <span className="display" style={{ fontSize: "1.35rem", fontWeight: 900, letterSpacing: "0.02em" }}>ORIORA</span>
          <span className="display" style={{ fontSize: "1.35rem", fontWeight: 700, letterSpacing: "0.06em", color: "var(--accent)" }}>WORKOUT</span>
        </div>
        <div className="row" style={{ gap: "0.9rem" }}>
          {weeklyGoal !== null && weeklyGoal > 0 && (
            <div className="row" style={{ gap: "0.5rem" }} title={`${last7Count} of ${weeklyGoal} sessions this week (your stated goal)`}>
              <ProgressRing value={last7Count} target={weeklyGoal} />
              <div className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.3 }}>this<br />week</div>
            </div>
          )}
          <BackupMenu
            appName="Workout Tracker" fileSlug="workout-tracker" appScheme="workout-tracker"
            hasData={workouts.length > 0} onRestored={reload}
            onExportCsv={doExportCsv}
          />
          <AccountMenu email={email} appSlug="workout-tracker" appName="Oriora Workout" />
          <AddFundsMenu />
        </div>
      </header>

      {/* LEAD — the domain's real promise: start (or repeat) today's training, one tap. This replaces
          the retired count/streak hero: the app now leads with the ACT of logging a session, not a
          report of how much has been logged. Analysis is demoted below the record (steps 1 & 3). */}
      <div className="card card-in" style={{ padding: "1.25rem 1.35rem", marginBottom: "1rem", borderTop: "3px solid var(--accent)" }}>
        <div className="display-eyebrow" style={{ color: "var(--accent)" }}>Today's training</div>
        {workouts.length > 0 ? (
          <>
            <div className="display" style={{ fontSize: "1.6rem", fontWeight: 900, margin: "0.3rem 0 0.15rem" }}>Start today's session</div>
            <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 0.9rem", lineHeight: 1.5 }}>Pick up where your record leaves off — repeat your last session with the same lifts, or start a fresh one.</p>
            <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
              <button className="btn btn-primary row" style={{ gap: "0.35rem", fontSize: "var(--fs-sm)" }} onClick={() => void repeatWorkout(workouts[0])}>
                <Repeat size={15} /> Repeat {workouts[0].name}
              </button>
              <button className="btn row" style={{ gap: "0.35rem", fontSize: "var(--fs-sm)" }} onClick={() => setShowNew(true)}><Plus size={15} /> New workout</button>
            </div>
          </>
        ) : (
          <>
            <div className="display" style={{ fontSize: "1.6rem", fontWeight: 900, margin: "0.3rem 0 0.15rem" }}>Today's session</div>
            <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 0.9rem", lineHeight: 1.5 }}>Nothing logged yet. The first set starts it — pick the lift, put in the weight and reps.</p>
            <FirstSet onLogged={() => { void reload(); }} />
            <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.85rem 0 0", lineHeight: 1.5 }}>
              {/* §7b: the device claim was unqualified though Cloud Backup ships. Qualifier added
                  2026-09-25, on the SAME line as the claim so the per-line gate matches. */}
              Everything on this screen is free, and it
              stays on this device unless you back it up: the plate calculator, the rest
              timer, uncapped routines, PRs and estimated 1RM, CSV export. Only the AI trend summary is
              pay-per-use.
            </p>
            <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.85rem" }}>
              <button className="btn row" style={{ gap: "0.35rem", fontSize: "var(--fs-sm)" }} onClick={() => setShowNew(true)}><Plus size={15} /> New workout</button>
            </div>
          </>
        )}
      </div>

      {/* At zero the gym tools come BEFORE the record, because they are the only thing on this
          screen that already works: the plate calculator needs no data at all, and it is the tool
          Strong puts behind Pro. Position only — the section itself is rendered unchanged, and
          below it nothing is reordered. */}
      {workouts.length === 0 && <ToolsSection />}

      {showNew && <NewWorkout onCancel={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); void reload(); setDetailId(id); }} />}

      {/* Program prescription — its "Next up" card is the session-when-due, kept high, just below the
          lead. (Its "Draft a program" AI offer is no longer the first thing a new user meets.) */}
      <ProgramSection
        refreshSignal={programRefresh}
        triggerDraft={programDraftTrigger}
        onProgramChanged={() => setProgramRefresh((n) => n + 1)}
        onStartSession={(session: ProgramSession) => void startFromProgramSession(session)}
      />

      {/* Free, on-device declining-lift board — part of the record, kept high (step 3). */}
      <AttentionBoard lifts={liftSignals} />

      {/* THE RECORD — the domain object, made visible. The count is demoted to a quiet secondary line. */}
      <div className="row" style={{ justifyContent: "space-between", margin: "1.5rem 0 0.75rem", flexWrap: "wrap", gap: "0.5rem", alignItems: "baseline" }}>
        <h2 className="display" style={{ fontSize: "1.15rem", margin: 0, letterSpacing: "0.03em" }}>The record</h2>
        <span className="muted display-eyebrow">{workouts.length} session{workouts.length !== 1 ? "s" : ""} recorded</span>
      </div>

      {workouts.length > 0 && (
        <div className="card" style={{ overflow: "hidden" }}>
          {workouts.map((w, i) => (
            <WorkoutRow key={w.id} w={w} delayMs={Math.min(i, 8) * 35} onOpen={() => setDetailId(w.id)}
              onRepeat={() => void repeatWorkout(w)}
              onDelete={async () => { await store.deleteWorkout(w.id); void reload(); }} />
          ))}
        </div>
      )}

      {/* AI trend summary — DEMOTED below the record (it describes the record; it never leads it).
          Same move solo_habit_tracker made ("R4 — the AI panel is DEMOTED below the record"). */}
      <div style={{ marginTop: "1.5rem" }}>
        <TrendInsights last14Count={last14.length} offline={offline} registerRun={(fn) => { summarizeRef.current = fn; }} />
      </div>

      <RoutinesSection routines={routines} onStart={startFromRoutine} onChanged={reload} />

      <PRBoard prs={prs} />

      {/* Gym tools — plate calculator + rest timer, FREE and on-device. Strong locks the plate
          calculator behind Pro; here it's the everyday math, no subscription. (At zero this same
          section is rendered higher instead — see the lead card.) */}
      {workouts.length > 0 && <ToolsSection />}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the list. */}
      {!chatOpen && (
        <button className="owc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <BarMark size={16} /> Chat
        </button>
      )}
      <aside className={`owc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="owc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to log, find, or summarize</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} onOpenProgramFlow={() => { setChatOpen(false); setProgramDraftTrigger((n) => n + 1); }} onRepeatLastWorkout={repeatLastWorkout} onStartRoutine={startRoutineById} />
      </aside>
    </main>
  );
}

// ── AI feature: 14-day training-trend summary (the LIVE Oriora brain) ──────────
// This is the app's one AI feature. It surfaces clear states for: nothing-to-analyze,
// loading, AI-paused (402 → add funds in your Oriora account), offline/unavailable,
// and an empty model result. The summary is observational — it never prescribes training.
function TrendInsights({
  last14Count, offline, registerRun,
}: {
  last14Count: number;
  offline: boolean;
  registerRun: (fn: () => void) => void;
}) {
  const [trends, setTrends] = useState<TrendSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ code: string; message: string } | null>(null);

  const run = useCallback(async () => {
    if (busy || last14Count === 0) return;
    setBusy(true); setErr(null); setTrends(null);
    try {
      // Compute the derived signals locally (Rust/SQLite); only these aggregates
      // are sent to the brain — no raw set-by-set history leaves the device.
      const signals = await store.trendSignals(14);
      if (!signals || signals.workouts === 0 || signals.total_sets === 0) {
        setErr({ code: "empty_input", message: "No sets in the last 14 days yet — log a few, then summarize." });
        return;
      }
      const r = await summarizeTrends(signals);
      setTrends(r.trends);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Could not summarize trends — please try again." });
    } finally {
      setBusy(false);
    }
  }, [busy, last14Count]);

  // Expose run() to the native ⌘T menu (registered once; ref inside Home stays current).
  useEffect(() => { registerRun(run); }, [registerRun, run]);

  const dirIcon = (d?: string) => (d === "increasing" ? TrendingUp : d === "decreasing" ? TrendingDown : Minus);
  const TrendIcon = dirIcon(trends?.volume_trend);
  const IntensityIcon = dirIcon(trends?.intensity?.direction);
  const hasMetrics = !!trends && (
    !!trends.volume_trend ||
    typeof trends.freq_per_week === "number" ||
    (!!trends.top_lifts && Object.keys(trends.top_lifts).length > 0) ||
    (!!trends.split && trends.split.groups.length > 0) ||
    !!trends.set_fatigue ||
    !!trends.intensity ||
    typeof trends.prs_in_window === "number"
  );

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.75rem" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem" }}>
          <Sparkles size={14} /> Training trends <span className="muted" style={{ fontWeight: 400 }}>· last 14 days</span>
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", gap: "0.3rem", flexShrink: 0 }}
          disabled={busy || last14Count === 0 || offline}
          title={offline ? "You're offline — reconnect to run AI" : last14Count === 0 ? "Log a workout in the last 14 days first" : "Summarize the last 14 days (⌘T)"}
          onClick={run}>
          {busy ? <><RefreshCw size={12} className="spin" /> Analyzing…</> : trends ? <><RefreshCw size={12} /> Re-run</> : "Summarize"}
        </button>
      </div>

      {/* Nothing to analyze yet */}
      {last14Count === 0 && !err && (
        <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.5rem 0 0" }}>Log a workout in the last 14 days to get an AI summary.</p>
      )}

      {/* Loading */}
      {busy && (
        <div className="row" style={{ marginTop: "0.6rem", gap: "0.5rem" }}>
          <div className="skeleton-line" style={{ width: "100%" }} />
        </div>
      )}

      {/* Error states — wallet (402), offline/unavailable, auth, BYOK, empty */}
      {err && !busy && (
        <div className="row" style={{ marginTop: "0.6rem", gap: "0.5rem", alignItems: "flex-start", fontSize: "var(--fs-xs)",
          color: err.code === "insufficient_funds" ? "var(--amber)" : "var(--rose)",
          background: err.code === "insufficient_funds" ? "color-mix(in srgb, var(--amber) 10%, transparent)" : "color-mix(in srgb, var(--rose) 8%, transparent)",
          border: `1px solid ${err.code === "insufficient_funds" ? "color-mix(in srgb, var(--amber) 32%, transparent)" : "color-mix(in srgb, var(--rose) 28%, transparent)"}`,
          borderRadius: 8, padding: "0.5rem 0.7rem" }}>
          {err.code === "insufficient_funds" ? <Wallet size={14} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />}
          <span style={{ lineHeight: 1.45 }}>{err.message}</span>
        </div>
      )}

      {/* Success — summary + derived-signal chips */}
      {trends && !busy && (
        <div style={{ marginTop: "0.6rem" }}>
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.55, margin: "0 0 0.6rem" }}>{trends.summary_text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {hasMetrics && (
            <div className="row" style={{ flexWrap: "wrap", gap: "0.5rem", fontSize: "var(--fs-xs)" }}>
              {trends.volume_trend && (
                <span className="row" style={{ gap: "0.3rem", background: "var(--elev-2)", borderRadius: 999, padding: "0.2rem 0.6rem", textTransform: "capitalize" }}>
                  <TrendIcon size={13} /> volume {trends.volume_trend}
                </span>
              )}
              {trends.intensity && (
                <span className="row" style={{ gap: "0.3rem", background: "var(--elev-2)", borderRadius: 999, padding: "0.2rem 0.6rem" }} title="Volume per minute, first vs last timed session">
                  <IntensityIcon size={13} /> intensity {formatWeight(trends.intensity.first)}→{formatWeight(trends.intensity.last)} /min
                </span>
              )}
              {typeof trends.freq_per_week === "number" && (
                <span className="row" style={{ gap: "0.3rem", background: "var(--elev-2)", borderRadius: 999, padding: "0.2rem 0.6rem" }}>
                  {trends.freq_per_week}× / week
                </span>
              )}
              {trends.split && trends.split.groups.length > 1 && (
                <span className="row" style={{ gap: "0.3rem", background: "var(--elev-2)", borderRadius: 999, padding: "0.2rem 0.6rem" }} title="Dominant movement group per session, in order">
                  split {trends.split.alternates ? "alternating" : "repeating"} · {trends.split.groups.join("→")}
                </span>
              )}
              {trends.set_fatigue && trends.set_fatigue.declining > 0 && (
                <span className="row" style={{ gap: "0.3rem", background: "var(--elev-2)", borderRadius: 999, padding: "0.2rem 0.6rem" }} title="Exercises whose reps dropped across sets within a session">
                  fatigue {trends.set_fatigue.declining}/{trends.set_fatigue.measured} lifts · −{formatWeight(trends.set_fatigue.avg_reps_drop)} reps
                </span>
              )}
              {typeof trends.prs_in_window === "number" && trends.prs_in_window > 0 && (
                <span className="row" style={{ gap: "0.3rem", background: "color-mix(in srgb, var(--amber) 14%, transparent)", color: "var(--amber)", borderRadius: 999, padding: "0.2rem 0.6rem", fontWeight: 600 }}>
                  <Trophy size={12} /> {trends.prs_in_window} PR{trends.prs_in_window !== 1 ? "s" : ""}
                </span>
              )}
              {trends.top_lifts && Object.entries(trends.top_lifts).map(([lift, w]) => (
                <span key={lift} className="row" style={{ gap: "0.3rem", background: "var(--accent-soft)", color: "var(--accent)", borderRadius: 999, padding: "0.2rem 0.6rem", fontWeight: 600 }} title="Best estimated 1RM in the window">
                  {lift}: {formatWeight(Number(w))}
                </span>
              ))}
            </div>
          )}
          {/* Receipts — the on-device facts the summary was drawn from (computed locally). */}
          {trends.facts && trends.facts.length > 0 && <FactList facts={trends.facts} />}
          {/* Observational guardrail — the AI describes patterns, it doesn't coach */}
          <div className="muted row" style={{ fontSize: "var(--fs-xs)", marginTop: "0.7rem", gap: "0.3rem", alignItems: "flex-start", lineHeight: 1.45 }}>
            <Info size={11} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>Observations only — describes patterns in your logged numbers; not training, medical, or fitness advice.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// The receipts: the deterministic, on-device facts the summary was drawn from. Each is
// a full sentence already containing the figures; the dot reflects its tone. Purely
// observational — these describe the user's own numbers, they never prescribe.
function FactList({ facts }: { facts: { kind: string; text: string; tone: FactTone }[] }) {
  const top = facts.slice(0, 5);
  if (top.length === 0) return null;
  const dotColor = (t: FactTone) => (t === "good" ? "var(--green)" : t === "watch" ? "var(--amber)" : "var(--ink-3)");
  return (
    <div style={{ marginTop: "0.7rem", paddingTop: "0.6rem", borderTop: "1px solid var(--border)" }}>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: "0.4rem" }}>
        The numbers behind this
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.35rem" }}>
        {top.map((f, i) => (
          <div key={i} className="row" style={{ gap: "0.5rem", alignItems: "flex-start", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
            <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: dotColor(f.tone), flexShrink: 0, marginTop: 6 }} />
            <span style={{ color: "var(--ink-2)" }}>{f.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// One workout row in the history list. If a photo was attached on-device (via chat) — e.g. a form or
// progress shot — it shows as a thumbnail with tap-to-zoom (the zoom overlay renders OUTSIDE the row).
// The photo is never sent to the model.
function WorkoutRow({ w, onOpen, onRepeat, onDelete, delayMs = 0 }: { w: Workout; onOpen: () => void; onRepeat: () => void; onDelete: () => void; delayMs?: number }) {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <div className="list-row row card-in" style={{ justifyContent: "space-between", borderTop: "1px solid var(--border)", padding: "0.75rem 1rem", cursor: "pointer", animationDelay: `${delayMs}ms` }}
        onClick={onOpen}>
        <div className="row" style={{ gap: "0.6rem", minWidth: 0 }}>
          {w.photo && (
            <img src={w.photo} alt={`Photo from ${w.name}`} title="Tap to view"
              onClick={(e) => { e.stopPropagation(); setZoom(true); }}
              style={{ width: 38, height: 38, objectFit: "cover", borderRadius: 8, cursor: "zoom-in", flexShrink: 0 }} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{w.name}</div>
            <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{fmtDate(w.performed_on)} · {w.total_sets} set{w.total_sets !== 1 ? "s" : ""} · {formatWeight(w.total_volume)} vol</div>
          </div>
        </div>
        <div className="row list-actions" style={{ gap: "0.3rem" }} onClick={(e) => e.stopPropagation()}>
          <button className="btn row" style={{ padding: "0.2rem 0.55rem", gap: "0.3rem", fontSize: "var(--fs-xs)", color: "var(--accent)" }} title="Repeat this workout — recreate it today with the same exercises"
            onClick={onRepeat}><Repeat size={13} /> Repeat</button>
          <button className="btn" style={{ padding: "0.2rem 0.4rem", color: "var(--rose)", display: "inline-flex" }} title="Delete workout"
            onClick={onDelete}><Trash2 size={14} /></button>
        </div>
      </div>
      {zoom && w.photo && (
        <PhotoViewer photos={[{ src: w.photo, alt: `Photo from ${w.name}` }]} onClose={() => setZoom(false)} />
      )}
    </>
  );
}

function NewWorkout({ onCancel, onCreated }: { onCancel: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [date, setDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create(ev: React.FormEvent) {
    ev.preventDefault(); setBusy(true); setErr(null);
    try {
      const w = await store.createWorkout({ name: name.trim() || "Workout", performed_on: date });
      onCreated(w.id);
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not create workout"); } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem", marginBottom: "1rem" }}>
      <form onSubmit={create} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
        <input placeholder="Workout name (e.g. Push day)" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="row" style={{ gridColumn: "1 / 3", justifyContent: "space-between" }}>
          <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err}</span>
          <div className="row" style={{ gap: "0.4rem" }}>
            <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Creating…" : "Start workout"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

// Reusable routines (templates): save a set of exercises you repeat, then start a
// workout from it in one tap. Purely a convenience list — logging is unchanged.
// ── READY STATE: the first set ────────────────────────────────────────────────
// a3 T1: the promise verb is "start today's training", and at zero the lead card read "Log your
// first session" above a button to a name-and-date form — two screens before a single set could
// exist. This is the same exercise·weight·reps line `ExerciseSession` logs with, hoisted onto the
// first screen: the session row and its first set are written together, so the record starts with
// a real set in it rather than an empty session. Same picker (`presetExercises` grouped by
// `muscleGroups`) and same commit rule (a weight OR reps is enough) as the in-session logger.
function FirstSet({ onLogged }: { onLogged: () => void }) {
  const [slug, setSlug] = useState(presetExercises[0].slug);
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const w = parseFloat(weight) || 0;
  const r = parseInt(reps, 10) || 0;
  const ready = w > 0 || r > 0;

  async function log() {
    if (!ready || busy) return;
    const p = presetBySlug(slug);
    if (!p) { setErr("Pick an exercise first."); return; }
    setBusy(true); setErr(null);
    try {
      const workout = await store.createWorkout({ name: "Today's session", performed_on: todayISO() });
      await store.addSet({ workout_id: workout.id, exercise_slug: p.slug, exercise_name: p.name, weight: w, reps: r, rpe: 0 });
      onLogged();
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not log that set"); setBusy(false); }
  }

  return (
    <div>
      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
        {/* `min-width: auto` on a flex item won't shrink below its widest option, and the library
            has names like "Lying Face Down Plate Neck Resistance" — the select ran off the card
            until minWidth was pinned to 0. It takes its own row; the numbers sit under it. */}
        <select aria-label="Exercise" value={slug} onChange={(e) => setSlug(e.target.value)} style={{ flex: "1 1 100%", width: "100%", minWidth: 0 }}>
          {muscleGroups.map((g) => {
            const items = presetExercises.filter((e) => e.muscleGroup === g);
            if (!items.length) return null;
            return <optgroup key={g} label={g}>{items.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}</optgroup>;
          })}
        </select>
        <input aria-label="Weight" inputMode="decimal" placeholder="kg" value={weight}
          onChange={(e) => setWeight(e.target.value)} style={{ flex: "0 0 68px", width: 68, textAlign: "center" }} />
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>×</span>
        <input aria-label="Reps" inputMode="numeric" placeholder="reps" value={reps}
          onChange={(e) => setReps(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void log(); }}
          style={{ flex: "0 0 68px", width: 68, textAlign: "center" }} />
        <button className="btn btn-primary" disabled={!ready || busy} style={{ fontSize: "var(--fs-sm)" }}
          onClick={() => void log()}>{busy ? "…" : "Log the set"}</button>
      </div>
      {err && <div style={{ color: "var(--rose)", fontSize: "var(--fs-xs)", marginTop: "0.4rem" }}>{err}</div>}
      <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.55rem 0 0", lineHeight: 1.5 }}>
        That set opens today's session; every set after it goes in the same place. One set is one
        number — PRs, estimated 1RM and the 14-day trends are read back out of what you log, never
        guessed between entries.
      </p>
    </div>
  );
}

// ── Gym tools — plate calculator + rest timer (pure client, free; Strong gates these) ──
function PlateCalculator() {
  const [unit, setUnit] = useState<"kg" | "lb">("kg");
  const [targetStr, setTargetStr] = useState("");
  const [barStr, setBarStr] = useState("");
  const bar = barStr.trim() ? Number(barStr) : (unit === "kg" ? DEFAULT_BAR_KG : DEFAULT_BAR_LB);
  const target = Number(targetStr);
  const valid = Number.isFinite(target) && target > 0 && Number.isFinite(bar) && bar >= 0;
  const res = valid ? plateBreakdown(target, bar, unit) : null;
  return (
    <div className="card" style={{ padding: "1.1rem 1.2rem" }}>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "0.7rem", alignItems: "center" }}>
        <Calculator size={16} style={{ color: "var(--accent)" }} />
        <span className="display" style={{ fontSize: "1rem", fontWeight: 800, letterSpacing: "0.02em" }}>Plate calculator</span>
      </div>
      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ flex: "1 1 120px", fontSize: "var(--fs-xs)" }}>
          <span className="muted">Target weight</span>
          <input inputMode="decimal" placeholder={unit === "kg" ? "e.g. 100" : "e.g. 225"} value={targetStr}
            onChange={(e) => setTargetStr(e.target.value)} style={{ width: "100%" }} />
        </label>
        <label style={{ flex: "1 1 90px", fontSize: "var(--fs-xs)" }}>
          <span className="muted">Bar ({unit})</span>
          <input inputMode="decimal" placeholder={String(unit === "kg" ? DEFAULT_BAR_KG : DEFAULT_BAR_LB)} value={barStr}
            onChange={(e) => setBarStr(e.target.value)} style={{ width: "100%" }} />
        </label>
        <div className="row" style={{ gap: 0, border: "1px solid var(--border, #ccc)", borderRadius: 8, overflow: "hidden" }}>
          {(["kg", "lb"] as const).map((u) => (
            <button key={u} className={unit === u ? "btn btn-primary" : "btn"} style={{ borderRadius: 0, padding: "0.4rem 0.7rem", fontSize: "var(--fs-xs)" }}
              onClick={() => setUnit(u)}>{u}</button>
          ))}
        </div>
      </div>
      {res && (
        <div style={{ marginTop: "0.85rem" }}>
          {res.perSide.length > 0 ? (
            <>
              <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.35rem" }}>Load per side:</div>
              <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
                {res.perSide.map((p, i) => (
                  <span key={i} style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)", borderRadius: 6, padding: "0.2rem 0.5rem", fontSize: "var(--fs-sm)", fontWeight: 700 }}>{p}</span>
                ))}
              </div>
            </>
          ) : (
            <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Just the bar — no plates needed.</div>
          )}
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.5rem" }}>
            Loads {res.loadable}{unit} ({res.perSideWeight}{unit}/side + {bar}{unit} bar){res.leftover > 0 ? ` · ${res.leftover}${unit} short of target with standard plates` : ""}
          </div>
        </div>
      )}
    </div>
  );
}

function RestTimer() {
  const PRESETS = [60, 90, 120, 180];
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const tickRef = useRef<number | null>(null);
  useEffect(() => {
    if (!running) return;
    tickRef.current = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { setRunning(false); try { navigator.vibrate?.(200); } catch { /* ignore */ } return 0; }
        return r - 1;
      });
    }, 1000);
    return () => { if (tickRef.current) window.clearInterval(tickRef.current); };
  }, [running]);
  const start = (secs: number) => { setRemaining(secs); setRunning(true); };
  const mmss = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;
  return (
    <div className="card" style={{ padding: "1.1rem 1.2rem" }}>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "0.7rem", alignItems: "center" }}>
        <Timer size={16} style={{ color: "var(--accent)" }} />
        <span className="display" style={{ fontSize: "1rem", fontWeight: 800, letterSpacing: "0.02em" }}>Rest timer</span>
      </div>
      <div className="display" style={{ fontSize: "2.1rem", fontWeight: 900, textAlign: "center", margin: "0.2rem 0 0.7rem", fontVariantNumeric: "tabular-nums", color: remaining > 0 ? "var(--ink-1, inherit)" : "var(--muted, #888)" }}>{mmss}</div>
      <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap", justifyContent: "center" }}>
        {PRESETS.map((s) => (
          // 90s read "1.5m 30s" — `${s/60}m` is not whole minutes. Minutes floor, seconds remainder.
          <button key={s} className="btn" style={{ fontSize: "var(--fs-xs)", padding: "0.4rem 0.7rem" }} onClick={() => start(s)}>{s < 60 ? `${s}s` : s % 60 === 0 ? `${s / 60}m` : `${Math.floor(s / 60)}m ${s % 60}s`}</button>
        ))}
        {remaining > 0 && (running
          ? <button className="btn row" style={{ gap: "0.3rem", fontSize: "var(--fs-xs)", padding: "0.4rem 0.7rem" }} onClick={() => setRunning(false)}><Pause size={13} /> Pause</button>
          : <button className="btn btn-primary row" style={{ gap: "0.3rem", fontSize: "var(--fs-xs)", padding: "0.4rem 0.7rem" }} onClick={() => setRunning(true)}><Play size={13} /> Resume</button>)}
        {remaining > 0 && <button className="btn row" style={{ gap: "0.3rem", fontSize: "var(--fs-xs)", padding: "0.4rem 0.7rem" }} onClick={() => { setRunning(false); setRemaining(0); }}><RotateCcw size={13} /> Reset</button>}
      </div>
    </div>
  );
}

function ToolsSection() {
  return (
    <div style={{ marginTop: "1.5rem" }}>
      <div className="row" style={{ justifyContent: "space-between", margin: "0 0 0.75rem", alignItems: "baseline" }}>
        <h2 className="display" style={{ fontSize: "1.15rem", margin: 0, letterSpacing: "0.03em" }}>Gym tools</h2>
        <span className="muted display-eyebrow">free · on-device</span>
      </div>
      <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        <PlateCalculator />
        <RestTimer />
      </div>
    </div>
  );
}

function RoutinesSection({ routines, onStart, onChanged }: { routines: Routine[]; onStart: (r: Routine) => void; onChanged: () => void }) {
  const [creating, setCreating] = useState(false);
  return (
    <section style={{ marginTop: "1.5rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h2 className="row" style={{ fontSize: "1rem", margin: 0, gap: "0.4rem" }}><ListChecks size={16} /> Routines</h2>
        {!creating && <button className="btn row" style={{ gap: "0.35rem", fontSize: "var(--fs-sm)" }} onClick={() => setCreating(true)}><Plus size={15} /> New routine</button>}
      </div>
      {creating && <RoutineForm onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); void onChanged(); }} />}
      {routines.length === 0 ? (
        !creating && <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: 0, lineHeight: 1.5 }}>Save a set of exercises you repeat (e.g. Push day) — then start a workout pre-loaded with them in one tap.</p>
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          {routines.map((r) => (
            <div key={r.id} className="list-row row" style={{ justifyContent: "space-between", borderTop: "1px solid var(--border)", padding: "0.7rem 1rem", gap: "0.75rem" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{r.name}</div>
                <div className="muted" style={{ fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.exercises.length > 0 ? r.exercises.map((e) => e.name).join(" · ") : "No exercises"}
                </div>
              </div>
              <div className="row" style={{ gap: "0.4rem", flexShrink: 0 }}>
                <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.7rem" }} onClick={() => onStart(r)}>Start</button>
                <button className="btn" style={{ padding: "0.2rem 0.4rem", color: "var(--rose)", display: "inline-flex" }} title="Delete routine"
                  onClick={async () => {
                    if (!(await confirmDestructive("Workout Tracker", `Delete routine "${r.name}"? This cannot be undone.`))) return;
                    await store.deleteRoutine(r.id); void onChanged();
                  }}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function RoutineForm({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [picks, setPicks] = useState<RoutineExercise[]>([]);
  const [slug, setSlug] = useState(presetExercises[0].slug);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function addPick() {
    const p = presetBySlug(slug);
    if (!p || picks.some((x) => x.slug === p.slug)) return;
    setPicks((cur) => [...cur, { slug: p.slug, name: p.name }]);
  }

  async function save(ev: React.FormEvent) {
    ev.preventDefault();
    if (picks.length === 0) { setErr("Add at least one exercise."); return; }
    setBusy(true); setErr(null);
    try {
      await store.createRoutine({ name: name.trim() || "Routine", exercises: picks });
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not save routine"); } finally { setBusy(false); }
  }

  return (
    <form onSubmit={save} className="card" style={{ padding: "1.1rem 1.25rem", marginBottom: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.6rem" }}>
      <input placeholder="Routine name (e.g. Push day)" value={name} onChange={(e) => setName(e.target.value)} />
      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        <select value={slug} onChange={(e) => setSlug(e.target.value)} style={{ flex: "2 1 200px", width: "auto" }}>
          {muscleGroups.map((g) => {
            const items = presetExercises.filter((e) => e.muscleGroup === g);
            if (!items.length) return null;
            return <optgroup key={g} label={g}>{items.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}</optgroup>;
          })}
        </select>
        <button type="button" className="btn row" style={{ gap: "0.3rem", fontSize: "var(--fs-sm)" }} onClick={addPick}><Plus size={14} /> Add</button>
      </div>
      {picks.length > 0 && (
        <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
          {picks.map((p) => (
            <span key={p.slug} className="chip row" style={{ gap: "0.3rem", fontSize: "var(--fs-xs)", alignItems: "center" }}>
              {p.name}
              <button type="button" onClick={() => setPicks((cur) => cur.filter((x) => x.slug !== p.slug))} title={`Remove ${p.name}`}
                style={{ background: "none", border: "none", color: "var(--rose)", cursor: "pointer", padding: 0, display: "inline-flex" }}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err}</span>
        <div className="row" style={{ gap: "0.4rem" }}>
          <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Save routine"}</button>
        </div>
      </div>
    </form>
  );
}

// Default rest between sets (seconds). Matches the category default (Strong's default Rest Timer
// is 2:00; 90s is a common working-set default) — this is the local, no-schema version of the
// rest timer APPSTORE.md advertises: it auto-starts when a set is logged and is skippable.
const REST_SECONDS = 90;

function WorkoutDetailView({ id, onBack, seedExercises }: { id: string; onBack: () => void; seedExercises?: RoutineExercise[] }) {
  const [detail, setDetail] = useState<WorkoutDetail | null>(null);
  // Rest timer — client-side only (no DB column): started on set completion, counts down, skippable.
  const [restLeft, setRestLeft] = useState(0);

  const reload = useCallback(async () => { setDetail(await store.getWorkout(id)); }, [id]);
  useEffect(() => { void reload(); }, [reload]);

  // One-second tick, re-armed each second; clears itself at zero. (setTimeout keyed on restLeft.)
  useEffect(() => {
    if (restLeft <= 0) return;
    const t = setTimeout(() => setRestLeft((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(t);
  }, [restLeft]);

  if (!detail) return <main style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</main>;
  const { workout, sets } = detail;

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: "max(2rem, calc(env(safe-area-inset-top) + 1rem)) max(1.5rem, env(safe-area-inset-right)) max(4rem, calc(env(safe-area-inset-bottom) + 2rem)) max(1.5rem, env(safe-area-inset-left))" }}>
      <button className="btn row" style={{ gap: "0.3rem", fontSize: "var(--fs-xs)", marginBottom: "1rem" }} onClick={onBack}><ChevronLeft size={14} /> Back</button>
      <h1 className="display" style={{ fontSize: "1.45rem", margin: "0 0 0.2rem", fontWeight: 900 }}>{workout.name}</h1>
      <div className="muted" style={{ fontSize: "var(--fs-sm)", marginBottom: "1.25rem" }}>{fmtDate(workout.performed_on)} · {sets.length} set{sets.length !== 1 ? "s" : ""} · {formatWeight(workout.total_volume)} total volume</div>

      {restLeft > 0 && (
        <div className="card-in row" role="timer" style={{ justifyContent: "space-between", background: "var(--accent)", color: "#fff", borderRadius: "var(--radius-button, 8px)", padding: "0.7rem 1rem", marginBottom: "1rem" }}>
          <div>
            <div className="display-eyebrow" style={{ color: "rgba(255,255,255,0.82)" }}>Resting</div>
            <div className="display tnum" style={{ fontSize: "1.5rem", fontWeight: 900, lineHeight: 1.05 }}>{Math.floor(restLeft / 60)}:{String(restLeft % 60).padStart(2, "0")}</div>
          </div>
          <button className="btn" style={{ background: "transparent", color: "#fff", borderColor: "rgba(255,255,255,0.55)" }} onClick={() => setRestLeft(0)}>Skip</button>
        </div>
      )}

      <SessionView workoutId={id} sets={sets} seedExercises={seedExercises} onChanged={reload} onRest={() => setRestLeft(REST_SECONDS)} />

      <button className="btn btn-primary row" style={{ marginTop: "1.25rem", width: "100%", justifyContent: "center", gap: "0.4rem" }}
        onClick={async () => { await store.finishWorkout(id); onBack(); }}>
        <Check size={16} /> Finish workout
      </button>
    </main>
  );
}

// ── The live in-session screen (a3 ASK #2) — the app's centre of gravity. Per-exercise SET ROWS,
// each anchored to the PREVIOUS session's weight × reps, each committed by a single checkmark tap
// that also fires the rest timer. The exercise <select> is demoted to "add an exercise". This
// replaces the old re-driven "Log a set" form. The session persists (finished_at NULL) so it is
// resumable from the home's "Resume today's session" card.
type PlannedRow = { key: string; weight: string; reps: string; rpe: string };
let __rowSeq = 0;
const rowKey = () => `r${++__rowSeq}`;

function SessionView({ workoutId, sets, seedExercises, onChanged, onRest }: {
  workoutId: string; sets: WorkoutSet[]; seedExercises?: RoutineExercise[];
  onChanged: () => void; onRest: () => void;
}) {
  const [manual, setManual] = useState<RoutineExercise[]>([]);
  const [addSlug, setAddSlug] = useState(presetExercises[0].slug);

  const committed = useMemo(() => groupByExercise(sets), [sets]);
  const committedBySlug = useMemo(() => {
    const m = new Map<string, WorkoutSet[]>();
    for (const g of committed) m.set(g.slug.toLowerCase(), g.sets);
    return m;
  }, [committed]);

  // Exercise order: the prescription (seed) first, then any committed exercise not already in it,
  // then exercises added by hand this session.
  const active = useMemo(() => {
    const order: RoutineExercise[] = [];
    const seen = new Set<string>();
    const add = (slug: string, name: string) => { const k = slug.toLowerCase(); if (!seen.has(k)) { seen.add(k); order.push({ slug, name }); } };
    for (const e of seedExercises ?? []) add(e.slug, e.name);
    for (const g of committed) add(g.slug, g.name);
    for (const e of manual) add(e.slug, e.name);
    return order;
  }, [seedExercises, committed, manual]);

  function addExercise(ev: React.FormEvent) {
    ev.preventDefault();
    const preset = presetBySlug(addSlug);
    if (!preset) return;
    setManual((m) => (m.some((x) => x.slug === addSlug) || committedBySlug.has(addSlug.toLowerCase()) ? m : [...m, { slug: addSlug, name: preset.name }]));
  }

  return (
    <div style={{ display: "grid", gap: "0.85rem" }}>
      {active.length === 0 && (
        <div className="card muted" style={{ padding: "1.4rem", textAlign: "center" }}>Add your first exercise below to start logging sets.</div>
      )}
      {active.map((ex) => (
        <ExerciseSession key={ex.slug} workoutId={workoutId} slug={ex.slug} name={ex.name}
          committed={committedBySlug.get(ex.slug.toLowerCase()) ?? []} onCommitted={onChanged} onRest={onRest} />
      ))}

      {/* Demoted: the exercise picker is now only "add an exercise to this session", never the per-set entry point. */}
      <form onSubmit={addExercise} className="card row" style={{ padding: "0.9rem 1rem", gap: "0.5rem", flexWrap: "wrap" }}>
        <span className="row muted" style={{ gap: "0.3rem", fontSize: "var(--fs-xs)", flexBasis: "100%" }}><Plus size={12} /> Add an exercise to this session</span>
        <select value={addSlug} onChange={(e) => setAddSlug(e.target.value)} style={{ flex: "2 1 200px", width: "auto" }}>
          {muscleGroups.map((g) => {
            const items = presetExercises.filter((e) => e.muscleGroup === g);
            if (!items.length) return null;
            return <optgroup key={g} label={g}>{items.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}</optgroup>;
          })}
        </select>
        <button type="submit" className="btn row" style={{ gap: "0.3rem" }}><Plus size={14} /> Add</button>
      </form>
    </div>
  );
}

function ExerciseSession({ workoutId, slug, name, committed, onCommitted, onRest }: {
  workoutId: string; slug: string; name: string; committed: WorkoutSet[];
  onCommitted: () => void; onRest: () => void;
}) {
  const preset = presetBySlug(slug);
  const tracksWeight = preset?.tracksWeight !== false;
  const tracksReps = preset?.tracksReps !== false;

  const [prev, setPrev] = useState<PrevSet[] | null>(null);
  useEffect(() => {
    let live = true;
    store.previousExerciseSets(slug, workoutId).then((pv) => { if (live) setPrev(pv); }).catch(() => { if (live) setPrev([]); });
    return () => { live = false; };
  }, [slug, workoutId]);

  // Planned (uncommitted) rows — local state, initialised ONCE the previous session is known so a
  // reload after a commit never wipes what the lifter is mid-typing. Sized to the previous session's
  // set count (at least one), each prefilled from the matching previous set (progressive-overload start).
  const [planned, setPlanned] = useState<PlannedRow[]>([]);
  const inited = useRef(false);
  const committedCount = committed.length;
  useEffect(() => {
    if (prev == null || inited.current) return;
    inited.current = true;
    const target = Math.max(prev.length, committedCount + 1);
    const rows: PlannedRow[] = [];
    for (let i = committedCount; i < target; i++) {
      const pv = prev[i] ?? prev[prev.length - 1] ?? null;
      rows.push({ key: rowKey(), weight: pv && tracksWeight ? String(pv.weight) : "", reps: pv && tracksReps ? String(pv.reps) : "", rpe: "" });
    }
    if (rows.length === 0) rows.push({ key: rowKey(), weight: "", reps: "", rpe: "" });
    setPlanned(rows);
  }, [prev, committedCount, tracksWeight, tracksReps]);

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [prFlash, setPrFlash] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const setField = (key: string, field: keyof PlannedRow, val: string) =>
    setPlanned((ps) => ps.map((r) => (r.key === key ? { ...r, [field]: val } : r)));

  async function commit(row: PlannedRow) {
    const w = parseFloat(row.weight) || 0;
    const r = parseInt(row.reps, 10) || 0;
    if (w <= 0 && r <= 0) { setErr("Enter a weight or reps first."); return; }
    setBusyKey(row.key); setErr(null); setPrFlash(null);
    try {
      const res = await store.addSet({ workout_id: workoutId, exercise_slug: slug, exercise_name: name, weight: w, reps: r, rpe: parseFloat(row.rpe) || 0 });
      setPlanned((ps) => ps.filter((x) => x.key !== row.key));
      onRest();       // one tap also fires the rest timer
      onCommitted();  // reload committed rows + header volume
      if (res.is_pr && res.pr_types.length) setPrFlash(prLabels(res.pr_types));
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not log set"); } finally { setBusyKey(null); }
  }

  function addPlanned() {
    const pv = prev && prev.length ? prev[prev.length - 1] : null;
    const last = planned[planned.length - 1];
    setPlanned((ps) => [...ps, {
      key: rowKey(),
      weight: last?.weight ?? (pv && tracksWeight ? String(pv.weight) : ""),
      reps: last?.reps ?? (pv && tracksReps ? String(pv.reps) : ""),
      rpe: "",
    }]);
  }

  async function removeCommitted(id: string) { await store.deleteSet(id); onCommitted(); }

  const prevSummary = prev && prev.length
    ? prev.map((pv) => (pv.weight > 0 ? `${formatWeight(pv.weight)}\u00d7${pv.reps}` : `${pv.reps}`)).join(", ")
    : null;

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="row" style={{ justifyContent: "space-between", padding: "0.65rem 1rem", background: "var(--elev-2)" }}>
        <div className="row" style={{ gap: "0.5rem" }}>
          <ExerciseIcon name={name} />
          <span style={{ fontWeight: 700 }}>{name}</span>
        </div>
        {prevSummary && <span className="muted tnum" style={{ fontSize: "var(--fs-xs)" }} title="Your previous session">{"\u21ba "}{prevSummary}</span>}
      </div>

      {/* committed sets (already logged this session) */}
      {committed.map((s, i) => (
        <div key={s.id} className="row" style={{ justifyContent: "space-between", borderTop: "1px solid var(--border)", padding: "0.5rem 1rem" }}>
          <span className="tnum" style={{ color: "var(--ink-3)", width: 26, fontWeight: 700 }}>{i + 1}</span>
          <span className="tnum" style={{ flex: 1, textAlign: "center", fontWeight: 600 }}>
            {s.weight > 0 ? `${formatWeight(s.weight)} \u00d7 ${s.reps}` : `${s.reps} reps`}
            {s.rpe > 0 && <span className="muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: "var(--fs-xs)" }}>@ RPE {s.rpe}</span>}
            {s.is_pr && s.pr_types.length > 0 && <span className="badge pr row" style={{ gap: "0.2rem", marginLeft: 6, display: "inline-flex", verticalAlign: "middle" }}><Trophy size={10} /> PR</span>}
          </span>
          <span className="row" style={{ gap: "0.4rem", alignItems: "center" }}>
            <Check size={15} style={{ color: "var(--accent)" }} aria-label="logged" />
            <button className="btn list-actions" style={{ padding: "0.15rem 0.3rem", color: "var(--rose)", display: "inline-flex" }} title="Delete set"
              onClick={async () => {
                if (!(await confirmDestructive("Workout Tracker", `Delete set ${i + 1} of ${name}? This cannot be undone.`))) return;
                await removeCommitted(s.id);
              }}><Trash2 size={12} /></button>
          </span>
        </div>
      ))}

      {/* planned rows — previous-session anchor + inputs + one-tap checkmark commit */}
      {planned.map((row, j) => {
        const idx = committedCount + j;
        const anchor = prev ? (prev[idx] ?? null) : null;
        return (
          <div key={row.key} className="row" style={{ flexWrap: "nowrap", borderTop: "1px solid var(--border)", padding: "0.45rem 0.8rem", gap: "0.35rem", alignItems: "center" }}>
            <span className="tnum" style={{ color: "var(--ink-3)", flex: "0 0 18px", fontWeight: 700, fontSize: "var(--fs-xs)" }}>{idx + 1}</span>
            <span className="muted tnum" style={{ flex: "0 0 42px", fontSize: 11, textAlign: "center", lineHeight: 1.1 }} title="Last time">{anchor ? (anchor.weight > 0 ? `${formatWeight(anchor.weight)}\u00d7${anchor.reps}` : `${anchor.reps}`) : "\u2014"}</span>
            {tracksWeight && <input type="number" inputMode="decimal" step="0.5" min="0" placeholder="kg" value={row.weight} onChange={(e) => setField(row.key, "weight", e.target.value)} style={{ flex: "1 1 52px", width: "auto", minWidth: 0, maxWidth: "5rem", padding: "0.4rem 0.4rem" }} />}
            <input type="number" inputMode="numeric" step="1" min="0" placeholder={tracksReps ? "reps" : "\u2014"} value={row.reps} disabled={!tracksReps} onChange={(e) => setField(row.key, "reps", e.target.value)} style={{ flex: "1 1 52px", width: "auto", minWidth: 0, maxWidth: "5rem", padding: "0.4rem 0.4rem" }} />
            <input type="number" inputMode="decimal" step="0.5" min="0" max="10" placeholder="RPE" title="RPE (optional, 6-10)" value={row.rpe} onChange={(e) => setField(row.key, "rpe", e.target.value)} style={{ flex: "0 0 44px", width: "auto", minWidth: 0, padding: "0.4rem 0.3rem" }} />
            <button className="btn btn-primary" style={{ flex: "0 0 auto", padding: "0.35rem 0.5rem", display: "inline-flex" }} title="Log this set (starts rest timer)" disabled={busyKey === row.key} onClick={() => void commit(row)}><Check size={16} /></button>
          </div>
        );
      })}

      <div className="row" style={{ padding: "0.5rem 1rem", justifyContent: "space-between", flexWrap: "wrap", gap: "0.4rem" }}>
        <button className="btn row" style={{ gap: "0.3rem", fontSize: "var(--fs-xs)", padding: "0.3rem 0.6rem" }} onClick={addPlanned}><Plus size={13} /> Add set</button>
        {prFlash && prFlash.length > 0 && <span className="row" style={{ gap: "0.3rem", color: "var(--accent)", fontWeight: 600, fontSize: "var(--fs-sm)" }}><Trophy size={14} /> New PR! {prFlash.join(" + ")}</span>}
      </div>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", padding: "0 1rem 0.6rem" }}>{err}</div>}
    </div>
  );
}

// Group a workout's flat set list by exercise (preserving first-seen order) — the gym-app
// domain view (Strong/Hevy): each exercise is its own set table with per-exercise volume.
type ExerciseGroupT = { slug: string; name: string; sets: WorkoutSet[]; volume: number };
function groupByExercise(sets: WorkoutSet[]): ExerciseGroupT[] {
  const order: string[] = [];
  const map = new Map<string, WorkoutSet[]>();
  for (const s of sets) {
    if (!map.has(s.exercise_slug)) { map.set(s.exercise_slug, []); order.push(s.exercise_slug); }
    map.get(s.exercise_slug)!.push(s);
  }
  return order.map((slug) => {
    const arr = map.get(slug)!;
    return { slug, name: arr[0].exercise_name, sets: arr, volume: arr.reduce((v, s) => v + s.weight * s.reps, 0) };
  });
}

function ExerciseGroup({ g, onChange }: { g: ExerciseGroupT; onChange: () => void }) {
  const best = g.sets.reduce((b, s) => (s.weight > (b?.weight ?? -1) ? s : b), null as WorkoutSet | null);
  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="row" style={{ justifyContent: "space-between", padding: "0.6rem 1rem", background: "var(--elev-2)" }}>
        <div className="row" style={{ gap: "0.5rem" }}>
          <ExerciseIcon name={g.name} />
          <span style={{ fontWeight: 700 }}>{g.name}</span>
        </div>
        <span className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>{g.sets.length} set{g.sets.length !== 1 ? "s" : ""}{g.volume > 0 ? ` · ${formatWeight(g.volume)}` : ""}</span>
      </div>
      {/* column header — the set table (Set · Weight×Reps · 1RM), Strong-style */}
      <div className="row" style={{ justifyContent: "space-between", padding: "0.3rem 1rem 0.2rem", fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--ink-3)" }}>
        <span>Set</span><span style={{ flex: 1, textAlign: "center" }}>Weight × Reps</span><span>Est. 1RM</span>
      </div>
      {g.sets.map((s, i) => (
        <div key={s.id} className="row" style={{ justifyContent: "space-between", borderTop: "1px solid var(--border)", padding: "0.5rem 1rem", background: s === best && g.sets.length > 1 ? "var(--accent-soft, transparent)" : undefined }}>
          <span className="tnum" style={{ color: "var(--ink-3)", width: 22, fontWeight: 700 }}>{i + 1}</span>
          <span className="tnum" style={{ flex: 1, textAlign: "center", fontWeight: 600 }}>
            {s.weight > 0 ? `${formatWeight(s.weight)} × ${s.reps}` : `${s.reps} reps`}
            {s.rpe > 0 && <span className="muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: "var(--fs-xs)" }}>@ RPE {s.rpe}</span>}
            {s.is_pr && s.pr_types.length > 0 && <span className="badge pr row" style={{ gap: "0.2rem", marginLeft: 6, display: "inline-flex", verticalAlign: "middle" }}><Trophy size={10} /> PR</span>}
          </span>
          <span className="row" style={{ gap: "0.5rem" }}>
            <span className="muted tnum" style={{ fontSize: "var(--fs-xs)", minWidth: 44, textAlign: "right" }}>{s.weight > 0 && s.reps > 0 ? formatWeight(roundedOneRepMax(s.weight, s.reps)) : "—"}</span>
            <button className="btn list-actions" style={{ padding: "0.15rem 0.3rem", color: "var(--rose)", display: "inline-flex" }} title="Delete set"
              onClick={async () => {
                if (!(await confirmDestructive("Workout Tracker", `Delete set ${i + 1} of ${g.name}? This cannot be undone.`))) return;
                await store.deleteSet(s.id); onChange();
              }}><Trash2 size={12} /></button>
          </span>
        </div>
      ))}
    </div>
  );
}

// Row 2b primitive — "which lifts need me", not "what exists". Free, on-device, no AI call:
// the same per-lift 14-day trend direction the paid summary already computes, surfaced directly
// as a ranked board instead of buried in a paid summary's aggregate "2/5 lifts" count. Declining
// lifts rank first (biggest drop first) — the ones a lifter would otherwise not notice until a
// session feels off. Matches the real competitive bar (Hevy's AI Coach "spots when your
// progression has stalled") but for free and instant, not gated behind a paid AI call.
export function rankDecliningLifts(lifts: LiftSignal[]): LiftSignal[] {
  return lifts
    .filter((l) => l.direction === "decreasing")
    .sort((a, b) => (b.first_top_set - b.last_top_set) - (a.first_top_set - a.last_top_set));
}

function AttentionBoard({ lifts }: { lifts: LiftSignal[] }) {
  const declining = rankDecliningLifts(lifts);
  if (declining.length === 0) return null;
  return (
    <div className="card card-in" style={{ padding: "1rem 1.25rem", marginTop: "1rem", borderLeft: "3px solid var(--rose)" }}>
      <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", marginBottom: "0.6rem" }}>
        <TrendingDown size={14} style={{ color: "var(--rose)" }} /> Needs attention <span className="muted" style={{ fontWeight: 400 }}>· top set trending down, last 14 days</span>
      </div>
      {declining.map((l) => {
        const drop = l.first_top_set - l.last_top_set;
        const pct = l.first_top_set > 0 ? Math.round((drop / l.first_top_set) * 100) : 0;
        return (
          <div key={l.exercise} className="row" style={{ justifyContent: "space-between", gap: "0.75rem", borderTop: "1px solid var(--border)", padding: "0.6rem 0" }}>
            <div className="row" style={{ gap: "0.55rem", minWidth: 0 }}>
              <ExerciseIcon name={l.exercise} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{l.exercise}</div>
                <div className="tnum muted" style={{ fontSize: "var(--fs-xs)" }}>{formatWeight(l.first_top_set)} → {formatWeight(l.last_top_set)}</div>
              </div>
            </div>
            <div className="row" style={{ gap: "0.65rem", flexShrink: 0 }}>
              {/* literal hex, not var(--rose) — SVG presentation attributes don't reliably resolve CSS custom properties in WKWebView */}
              {l.history.length >= 2 && <Sparkline points={l.history} color="#dc2626" />}
              <span className="tnum" style={{
                fontWeight: 700, fontSize: "var(--fs-sm)", color: "var(--rose)",
                background: "color-mix(in srgb, var(--rose) 12%, transparent)", borderRadius: 999, padding: "0.2rem 0.55rem",
              }}>
                −{pct}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// A hand-rolled on-device SVG line of estimated-1RM over time for one exercise (no chart lib).
// Pure display of the customer's own logged history — never a projection or a target.
function ExerciseProgressChart({ points, color }: { points: SessionPoint[]; color: string }) {
  if (points.length < 2) {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", padding: "0.1rem 1rem 0.7rem" }}>Log this lift on another day to see the line build.</div>;
  }
  const W = 300, H = 76, padX = 8, padTop = 10, padBot = 14;
  const vals = points.map((pt) => pt.est_one_rm);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const n = points.length;
  const x = (i: number) => padX + (n === 1 ? 0 : (i / (n - 1)) * (W - 2 * padX));
  const y = (v: number) => padTop + (1 - (v - min) / span) * (H - padTop - padBot);
  const d = points.map((pt, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)} ${y(pt.est_one_rm).toFixed(1)}`).join(" ");
  return (
    <div style={{ padding: "0.1rem 1rem 0.7rem" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Estimated 1RM over time" style={{ display: "block" }}>
        <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {points.map((pt, i) => <circle key={i} cx={x(i)} cy={y(pt.est_one_rm)} r={2.3} fill={color} />)}
      </svg>
      <div className="row muted tnum" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)", marginTop: 2 }}>
        <span>{fmtDate(points[0].performed_on)}</span>
        <span>est 1RM {formatWeight(min)} → {formatWeight(max)}</span>
        <span>{fmtDate(points[n - 1].performed_on)}</span>
      </div>
    </div>
  );
}

function PRBoard({ prs }: { prs: ExercisePR[] }) {
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [hist, setHist] = useState<Record<string, SessionPoint[]>>({});
  if (prs.length === 0) return null;
  const toggle = async (slug: string) => {
    const next = openSlug === slug ? null : slug;
    setOpenSlug(next);
    if (next && !hist[next]) {
      try { const h = await store.exerciseHistory(next); setHist((m) => ({ ...m, [next]: h })); } catch { /* chart just stays closed */ }
    }
  };
  return (
    <>
      <h2 className="row" style={{ fontSize: "1rem", margin: "1.75rem 0 0.75rem", gap: "0.4rem" }}><Trophy size={16} /> Personal records</h2>
      <div className="card card-in" style={{ overflow: "hidden" }}>
        {prs.map((p) => {
          const { color } = exerciseVisual(p.exercise_name);
          const open = openSlug === p.exercise_slug;
          return (
            <div key={p.exercise_slug} style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => void toggle(p.exercise_slug)} aria-expanded={open}
                title="Show progression over time"
                className="row" style={{ width: "100%", justifyContent: "space-between", gap: "0.75rem", padding: "0.65rem 1rem", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                <div className="row" style={{ gap: "0.55rem", minWidth: 0 }}>
                  <span aria-hidden style={{ display: "inline-block", transition: "transform .15s", transform: open ? "rotate(90deg)" : "none", color: "var(--ink-3)", fontSize: "0.85rem" }}>›</span>
                  <ExerciseIcon name={p.exercise_name} />
                  <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{p.exercise_name}</span>
                </div>
                <div className="row tnum" style={{ gap: "1.1rem", flexShrink: 0 }}>
                  <span title="Heaviest set" style={{ textAlign: "right" }}>
                    <b style={{ fontSize: "1.15rem", color }}>{formatWeight(p.heaviest_weight)}</b>
                    <span className="muted" style={{ fontSize: "var(--fs-xs)" }}> × {p.heaviest_weight_reps}</span>
                  </span>
                  <span className="muted" style={{ fontSize: "var(--fs-xs)", textAlign: "right", minWidth: 56 }} title="Estimated 1RM">1RM<br />{formatWeight(p.estimated_one_rm)}</span>
                </div>
              </button>
              {open && (hist[p.exercise_slug]
                ? <ExerciseProgressChart points={hist[p.exercise_slug]} color={color} />
                : <div className="muted" style={{ fontSize: "var(--fs-xs)", padding: "0.1rem 1rem 0.7rem" }}>Loading…</div>)}
            </div>
          );
        })}
      </div>
    </>
  );
}
